import { useCallback } from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { useQueryParamValue } from 'src/app/shared/hooks/location';

import { PREVIEW_CLOSE_EVENT, SUGGESTED_TEMPLATE_NOTICE } from '../../../../../../constants';
import { useCreatePetitionUpdateContext } from '../../../../context';
import type { PreviewEmailModalBodyProps } from '../../../../types';
import { PreviewEmailContentBox } from '../PreviewEmailContentBox';
import { PreviewEmailModalHeader } from '../PreviewEmailModalHeader';
import { PreviewEmailPetitionTitle } from '../PreviewEmailPetitionTitle';
import { PreviewEmailSenderInfo } from '../PreviewEmailSenderInfo';

import { ModalCloseButton } from './ModalCloseButton';

export function PreviewEmailModalBody({
	emailTitle,
	emailContent,
	petitionTitle,
	userName,
	imageDataUrl,
	onClose,
}: PreviewEmailModalBodyProps): JSX.Element {
	const track = useTracking();
	const sourceLocation = useQueryParamValue('source_location');
	const isTemplateFilled = useCreatePetitionUpdateContext();
	const closeModal = useCallback(() => {
		void track(PREVIEW_CLOSE_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
			victory: false,
			edit: false,
		});
		onClose();
	}, [track, isTemplateFilled, sourceLocation, onClose]);
	return (
		<Flex
			sx={{
				flexDirection: 'column',
				flexWrap: 'wrap',
			}}
			pt={40}
			pb={[16, 32]}
		>
			<ModalCloseButton data-qa="petition-update-email-preview-close-modal" onClick={() => closeModal()} />
			<PreviewEmailModalHeader />
			<Box variant="bordered" p={[16, 24]} mb={[16, 32]} sx={{ borderRadius: 0, borderColor: 'neutral-grey100' }}>
				<Flex sx={{ flexDirection: 'column' }}>
					<Box>
						<Text as="h2" mt={0} mb={16} fontWeight="normal" size="huge" sx={{ fontFamily: 'Helvetica Neue' }}>
							{emailTitle}
						</Text>
					</Box>
					<PreviewEmailSenderInfo userName={userName} />
					<PreviewEmailPetitionTitle title={petitionTitle} userName={userName} />
					<PreviewEmailContentBox subject={emailTitle} body={emailContent} imageDataUrl={imageDataUrl} />
				</Flex>
			</Box>
			<Box sx={{ alignSelf: [null, 'center'] }}>
				<Button onClick={() => closeModal()} sx={{ width: ['100%', 'auto'] }}>
					<Translate value="fe.pages.petition_update_composer.preview.close_button" />
				</Button>
			</Box>
		</Flex>
	);
}
